/**
 * Block 13 PR-G Phase 4A → Phase 6.5D — нижние CTA-кнопки 2.13.2.
 *
 * 3 кнопки vertical (P-019):
 *   - **Сохранить** — auto-counter naming «Договор №{N} от {date}» +
 *     saveContractWithState(savedContractsIdb) + clear draft на success.
 *   - **Скачать PDF** — generateAndDownloadContractPdf (Phase 2 service).
 *   - **Переслать клиенту** — opens `<ShareContractBottomSheet/>` (Phase 5
 *     component, wired via 6.5D Item #7 for Block 12 parity).
 *
 * **Phase 6.5D Item #6 (Aibek decision 2026-05-24 — REVERSAL of 6.5A):**
 * PDF / Share buttons **всегда активны** (не gated на selectIsDraftReadyForPdf).
 * Use case: Aibek хочет отправить пустой бланк договора клиенту чтобы тот
 * заполнил поля сам перед подписанием. PDF render обрабатывает empty fields
 * gracefully (`substituteUnderscores` replaces with empty strings). Save
 * остаётся gated на selectCanSaveContract (≥1 поле заполнено).
 *
 * MissingFieldsBadge + readiness hint остаются как **informational** (не
 * блокируют actions) — soft guidance per P-021 «5 states» empty-state pattern.
 *
 * **Phase 6.5D Item #7 (Aibek decision 2026-05-24 — Block 12 parity):**
 * Share button opens `<ShareContractBottomSheet/>` (already existed since
 * Phase 5) вместо прямого вызова `shareContractPdf({channel:'auto'})`. Sheet
 * предлагает 5 channels (WhatsApp/Telegram/MAX/Mail/Gmail) на desktop +
 * single «Поделиться» CTA на mobile (responsive через `useMediaQuery`).
 *
 * Reference:
 *   - Block 12 `features/calculator/quote/sections/BottomActionButtons.tsx`
 *     — pattern source (share state + sheet wiring).
 *   - Phase 5 `features/contract/components/ShareContractBottomSheet.tsx`
 *     — frozen, no changes needed.
 *   - PRD §10.13.2 «CTA внизу 2.13.2»
 */

import { useState, useId, useMemo, type Dispatch } from 'react';
import { Download, Save, Send } from 'lucide-react';

import { useAuthStore } from '@/lib/stores/authStore';

import { ShareContractBottomSheet } from '../components/ShareContractBottomSheet';
import { generateAndDownloadContractPdf } from '../services/generateContractPdf';
import type { ContractShareResult } from '../services/shareContractPdf';
import { getNextContractCounter } from '../state/contractCounter';
import { clearContractDraftFromIdb } from '../state/contractDraftIdbSync';
import {
  hasContractNumConflict,
  loadSavedContract,
  saveContractWithState,
} from '../state/savedContractsIdb';
import {
  selectCanSaveContract,
  selectIsDraftReadyForPdf,
  selectMissingSectionsForPdf,
} from '../state/contractSelectors';
import { useContractDraftStore } from '../state/useContractDraftStore';
import { loadContractTemplate } from '../templates/loadContractTemplate';
import type { ContractAction, ContractFormState } from '../state/contractReducer';

type SaveStatus = 'idle' | 'saving' | 'success' | 'error';
type PdfStatus = 'idle' | 'rendering' | 'error';

export interface BottomActionsProps {
  state: ContractFormState;
  /**
   * PR-B1 (ADR-0034): session-end clear перенесён в `ContractFormContainer`
   * (navigate-Home / beforeunload). BottomActions больше НЕ dispatch'ит
   * `CLEAR_CUSTOMER_REQUISITES_VALUES` на PDF/Share. Проп сохранён для
   * caller-совместимости (`ContractFormView`) и как unit-guard «PDF/Share
   * не очищают реквизиты».
   */
  dispatch: Dispatch<ContractAction>;
  /** Уникальный ключ для draft store / IDB sync. Обычно contractId. */
  draftKey: string;
}

const CTA_PRIMARY_CLASS =
  'w-full min-h-16 rounded-2xl bg-cta text-ink text-lg font-semibold flex items-center justify-center gap-3 ' +
  'disabled:opacity-50 disabled:cursor-not-allowed focus-visible:ring-2 focus-visible:ring-cta focus-visible:outline-none active:opacity-90';

const CTA_SECONDARY_CLASS =
  'w-full min-h-14 rounded-2xl border-[1.5px] border-accent bg-card text-ink text-base font-medium flex items-center justify-center gap-3 ' +
  'disabled:opacity-50 disabled:cursor-not-allowed focus-visible:ring-2 focus-visible:ring-cta focus-visible:outline-none active:opacity-90';

/**
 * Compose `aria-describedby` value из массива (id | falsy) — фильтрует falsy +
 * join'ит spaces. Возвращает undefined если ничего не set (чтобы не emit'ить
 * пустой `aria-describedby=""`).
 */
function describedByIds(...ids: Array<string | false | null | undefined>): string | undefined {
  const filtered = ids.filter((x): x is string => typeof x === 'string' && x !== '');
  return filtered.length > 0 ? filtered.join(' ') : undefined;
}

/** Pretty Russian date «23 мая 2026 г.» для name «Договор №N от 23 мая 2026 г.» */
function formatRussianDate(iso: string): string {
  // ISO YYYY-MM-DD; iso может быть empty → fallback к today's date locale.
  const d = iso ? new Date(iso + 'T00:00:00') : new Date();
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString('ru-RU', { day: 'numeric', month: 'long', year: 'numeric' });
}

export function BottomActions({ state, draftKey }: BottomActionsProps) {
  // PR-B1 (ADR-0034): customer requisites clear перенесён с PDF-delivered на
  // session-end (см. ContractFormContainer). BottomActions больше не использует
  // dispatch — проп остаётся в интерфейсе для caller-compat (см. JSDoc выше).

  const userId = useAuthStore((s) => s.profile?.phone) ?? 'anon-user';

  const [saveStatus, setSaveStatus] = useState<SaveStatus>('idle');
  const [savedName, setSavedName] = useState<string | null>(null);
  const [pdfStatus, setPdfStatus] = useState<PdfStatus>('idle');
  const [shareSheetOpen, setShareSheetOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [shareNotification, setShareNotification] = useState<string | null>(null);

  const errorId = useId();
  const hintId = useId();

  const canSave = selectCanSaveContract(state) && saveStatus !== 'saving';
  // Phase 6.5D Item #6 — PDF / Share больше не gated на readiness. Только
  // блокируем во время active operation (avoid double-trigger).
  const canDownloadPdf = pdfStatus !== 'rendering';
  const canShare = !shareSheetOpen;

  // Phase 6.5D Item #7 — template loaded once и passed to share sheet. Memoize
  // чтобы не re-load JSON при каждом re-render BottomActions.
  const template = useMemo(() => loadContractTemplate(state.templateKey), [state.templateKey]);

  async function handleSave(): Promise<void> {
    if (!selectCanSaveContract(state)) {
      setErrorMessage('Заполните хотя бы одно поле перед сохранением');
      return;
    }
    setSaveStatus('saving');
    setErrorMessage(null);

    try {
      // Phase 7 hotfix round 5 (R5-#1, 2026-05-26) — uniqueness check для
      // user-typed contractNum. Empty contractNum пропускается — counter
      // fallback гарантирует uniqueness атомарным increment'ом.
      if (state.contractNum.trim() !== '') {
        const conflict = await hasContractNumConflict(state.contractNum, state.id);
        if (conflict) {
          setSaveStatus('error');
          setErrorMessage('Договор с таким номером уже есть');
          return;
        }
      }

      // Phase 7 hotfix round 6 (R6-#2, 2026-05-26) — naming logic update:
      // user-typed contractNum ВСЕГДА regenerate'ит name (даже на re-save с
      // изменённым номером). Counter не consume в user-typed path. Empty
      // contractNum + existing → preserve existing.name (counter не consume
      // повторно). Empty contractNum + new → counter fallback.
      //
      // Background (R5-#1 regression): прежний `if (existing) name = existing.name`
      // защищал от counter inflation, но также blocked update name когда user
      // менял contractNum на существующем договоре (Aibek visual verify round 11).
      const existing = await loadSavedContract(state.id);
      const userTyped = state.contractNum.trim();
      const dateLabel = formatRussianDate(state.contractDate);
      let name: string;
      if (userTyped) {
        // User задал contractNum (либо новый, либо изменённый) → regenerate.
        name = `Договор №${userTyped} от ${dateLabel}`;
      } else if (existing) {
        // Re-save без user contractNum → preserve existing.name (не consume
        // counter повторно для того же draft'а).
        name = existing.name;
      } else {
        // New save без user contractNum → counter fallback (atomicity gives
        // uniqueness).
        const counter = await getNextContractCounter();
        name = `Договор №${counter} от ${dateLabel}`;
      }
      const now = Date.now();
      const updatedState: ContractFormState = { ...state, updatedAt: now };

      await saveContractWithState({
        id: state.id,
        userId,
        name,
        state: updatedState,
        createdAt: existing?.createdAt ?? state.createdAt,
        updatedAt: now,
      });

      // Clear draft (mirror Block 12 pattern) — next mount стартует с blank/seed.
      useContractDraftStore.getState().clearDraft(draftKey);
      await clearContractDraftFromIdb(draftKey);

      setSavedName(name);
      setSaveStatus('success');
    } catch (err) {
      if (import.meta.env.DEV) {
        // P-009: log only err.name (DOMException может содержать record data).
        const errName = err instanceof Error ? err.name : 'unknown';
        console.error('[BottomActions] handleSave failed', errName);
      }
      setSaveStatus('error');
      setErrorMessage('Не удалось сохранить. Попробуйте ещё раз.');
    }
  }

  async function handleDownloadPdf(): Promise<void> {
    // Phase 6.5D Item #6 — no readiness gate. PDF render обрабатывает empty
    // fields через `substituteUnderscores` (заменяет _ на empty string), что
    // позволяет отправлять пустой бланк клиенту для заполнения.
    setPdfStatus('rendering');
    setErrorMessage(null);

    try {
      await generateAndDownloadContractPdf(state, template);
      // PR-B1 (ADR-0034): clear trigger перенесён с PDF-delivered на session-end.
      // Реквизиты клиента сохраняются между итерациями PDF в рамках сессии —
      // очистка происходит в ContractFormContainer на navigate-Home / beforeunload
      // (если нет opt-in preserveCustomerRequisites).
      setPdfStatus('idle');
    } catch (err) {
      if (import.meta.env.DEV) {
        const errName = err instanceof Error ? err.name : 'unknown';
        console.error('[BottomActions] handleDownloadPdf failed', errName);
      }
      setPdfStatus('error');
      setErrorMessage('Не удалось создать PDF. Попробуйте ещё раз.');
    }
  }

  function handleShareResult(result: ContractShareResult): void {
    // PR-B1 (ADR-0034): customer requisites clear перенесён с PDF-delivered
    // (shared / downloaded / desktop-direct) на session-end. Здесь больше нет
    // dispatch CLEAR — реквизиты сохраняются до выхода (см. ContractFormContainer).
    // Status semantics ниже сохранены для notifications/error handling.
    if (result.status === 'shared') {
      setShareNotification('Договор отправлен');
    } else if (result.status === 'downloaded') {
      setShareNotification('PDF скачан. Прикрепите его к сообщению клиенту.');
    } else if (result.status === 'error') {
      setErrorMessage('Не удалось отправить. Попробуйте ещё раз.');
    }
    // 'cancelled' / 'desktop-direct' — silent notification (sheet сам показывает feedback).
  }

  // Phase 6.5D Item #6 — readiness hint остаётся как **informational** signal,
  // не блокирует кнопки. Покажет список пустых required секций (Стороны /
  // Стоимость) если есть, но user может всё равно нажать PDF / Share.
  const showReadinessHint = !selectIsDraftReadyForPdf(state);
  const missingSections = selectMissingSectionsForPdf(state);
  const missingSectionsLabel = missingSections.join(', ');

  return (
    <div
      className="mx-3 mt-1 flex flex-col gap-2.5"
      data-testid="contract-bottom-actions"
      aria-label="Действия по договору"
    >
      {/* Phase 6.5 #15 — порядок кнопок per Aibek decision:
          Сохранить (PRIMARY) → Скачать PDF → Переслать клиенту.
          Save = safest, most-common action, потому primary at top. */}
      <button
        type="button"
        onClick={handleSave}
        disabled={!canSave}
        aria-label="Сохранить договор"
        aria-describedby={describedByIds(errorMessage && errorId)}
        data-testid="contract-save-button"
        className={CTA_PRIMARY_CLASS}
      >
        <Save size={22} aria-hidden="true" />
        {saveStatus === 'saving'
          ? 'Сохраняется…'
          : saveStatus === 'success'
            ? '✓ Сохранён'
            : 'Сохранить'}
      </button>

      <button
        type="button"
        onClick={handleDownloadPdf}
        disabled={!canDownloadPdf}
        aria-label="Скачать PDF"
        aria-describedby={describedByIds(showReadinessHint && hintId, errorMessage && errorId)}
        data-testid="contract-download-pdf-button"
        className={CTA_SECONDARY_CLASS}
      >
        <Download size={22} aria-hidden="true" />
        {pdfStatus === 'rendering' ? 'Создаётся PDF…' : 'Скачать PDF'}
      </button>

      <button
        type="button"
        onClick={() => {
          setShareNotification(null);
          setShareSheetOpen(true);
        }}
        disabled={!canShare}
        aria-label="Переслать клиенту"
        aria-describedby={describedByIds(showReadinessHint && hintId, errorMessage && errorId)}
        data-testid="contract-share-button"
        className={CTA_SECONDARY_CLASS}
      >
        <Send size={22} aria-hidden="true" />
        Переслать клиенту
      </button>

      {showReadinessHint ? (
        <p
          id={hintId}
          className="text-sm text-ink text-center font-medium"
          data-testid="contract-readiness-hint"
        >
          Заполните: {missingSectionsLabel}
        </p>
      ) : null}

      {saveStatus === 'success' && savedName ? (
        <p
          role="status"
          aria-live="polite"
          data-testid="contract-save-success"
          className="text-sm text-ink font-semibold text-center"
        >
          Сохранён: <strong>{savedName}</strong>
        </p>
      ) : null}

      {shareNotification ? (
        <p
          role="status"
          aria-live="polite"
          data-testid="contract-share-notification"
          className="text-sm text-ink text-center"
        >
          {shareNotification}
        </p>
      ) : null}

      {errorMessage ? (
        <p
          id={errorId}
          role="alert"
          data-testid="contract-action-error"
          className="text-sm text-destructive font-medium text-center"
        >
          {errorMessage}
        </p>
      ) : null}

      <ShareContractBottomSheet
        isOpen={shareSheetOpen}
        onClose={() => setShareSheetOpen(false)}
        draft={state}
        template={template}
        onResult={handleShareResult}
      />
    </div>
  );
}
